import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { Col, Skeleton } from 'antd';
import moment from 'moment';
import { useTheme } from '../../../context/ThemeContext';
import { StyledCard, StatsGrid, StatCard, StyledStatistic, CardTitle, StatisticSubtext } from './Styles';
import { addCommasInPrices } from './constants';
import { MonthlyInsightsSectionProps } from './types';


const MonthlyInsightsSection: React.FC<MonthlyInsightsSectionProps> = ({
  reportData,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  const insights = useMemo(() => {
    if (!reportData?.salesSummary || !reportData?.period?.startDate) {
      return null;
    }
    const salesSummary = reportData.salesSummary;
    const grossSales = salesSummary.grossSales || 0;
    const netSales = salesSummary.netSales || 0;
    const orderCount = salesSummary.orderCount || 0;
    const daysInMonth = moment(reportData.period.startDate).daysInMonth();
    const profitMargin = grossSales > 0 ? ((netSales / grossSales) * 100) : 0;
    const averageOrderValue = orderCount > 0 ? (grossSales / orderCount) : 0;
    const dailyAverage = grossSales / daysInMonth;
    const ordersPerDay = orderCount / daysInMonth;

    return { profitMargin, averageOrderValue, dailyAverage, ordersPerDay };
  }, [reportData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <StyledCard title={<CardTitle level={4}>Monthly Performance Insights</CardTitle>}>
          <StatsGrid gutter={[16, 16]}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <StatCard>
                  <Skeleton active paragraph={{ rows: 1 }} />
                </StatCard>
              </Col>
            ))}
          </StatsGrid>
        </StyledCard>
      </ThemeProvider>
    );
  }

  if (!insights) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <StyledCard title={<CardTitle level={4}>Monthly Performance Insights</CardTitle>}>
        <StatsGrid gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <StatCard>
              <StyledStatistic
                title="Profit Margin"
                value={`${insights.profitMargin.toFixed(2)}%`}
              />
              <StatisticSubtext type="secondary">
                Net sales vs gross sales
              </StatisticSubtext>
            </StatCard>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard>
              <StyledStatistic
                title="Average Order Value"
                value={`$${addCommasInPrices(insights.averageOrderValue)}`}
              />
              <StatisticSubtext type="secondary">
                Per order average
              </StatisticSubtext>
            </StatCard>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard>
              <StyledStatistic
                title="Daily Average"
                value={`$${addCommasInPrices(insights.dailyAverage)}`}
              />
              <StatisticSubtext type="secondary">
                Average daily sales
              </StatisticSubtext>
            </StatCard>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard>
              <StyledStatistic
                title="Orders per Day"
                value={insights.ordersPerDay.toFixed(1)}
              />
              <StatisticSubtext type="secondary">
                Average daily orders
              </StatisticSubtext>
            </StatCard>
          </Col>
        </StatsGrid>
      </StyledCard>
    </ThemeProvider>
  );
};

export default MonthlyInsightsSection;
